<template>
  <v-dialog
    v-model="isLoading"
    fullscreen
    transition="dialog-bottom-transition"
    scrollable
    persistent
  >
    <v-card class="loader-shell" tile elevation="0" rounded="0">
      <header class="loader-head">
        <div class="loader-head__inner">
          <div class="loader-eyebrow">
            <span class="loader-dot"></span>
            WAIZMANNTABELLE&nbsp;/&nbsp;ANTRAGANNAHMECHECK
          </div>
          <h1 class="loader-title">
            Anonymer AntragAnnahmecheck
            <span class="loader-title__sep">·</span>
            <span class="loader-title__hint">bitte haben Sie kurz Geduld</span>
          </h1>
          <p class="loader-sub">
            Wir prüfen anonym die <strong>Antrags-Annahmebedingungen</strong>
            der relevanten Versicherer. Sie erfahren in wenigen Sekunden,
            welche Zahnzusatzversicherungen Ihren Antrag
            <strong class="text-pos">annehmen</strong> bzw.
            <strong class="text-neg">ablehnen</strong> würden.
          </p>

          <div class="loader-meta">
            <div class="meta-item">
              <span class="meta-label">Geprüfte Kriterien</span>
              <span class="meta-value"
                >{{ totalChecked }} / {{ totalTarget }}</span
              >
            </div>
            <div class="meta-item">
              <span class="meta-label">Fortschritt</span>
              <span class="meta-value">{{ overallPercent }}&nbsp;%</span>
            </div>
            <div class="meta-item meta-item--badge">
              <v-icon icon="mdi-lock-outline" size="14" class="mr-1" />
              100&nbsp;% anonym
            </div>
          </div>

          <v-progress-linear
            :model-value="overallPercent"
            color="primary"
            height="4"
            rounded="0"
            class="loader-overall"
          />
        </div>
      </header>

      <div class="loader-list">
        <div
          v-for="row in rows"
          :key="row.bar"
          class="check-row"
          :class="{ 'check-row--done': progressBars[row.bar].value >= 100 }"
        >
          <div class="check-row__main">
            <div class="check-row__label">{{ row.label }}</div>
            <div class="check-row__value">{{ row.value }}</div>
          </div>

          <div class="check-row__bar-wrap">
            <v-progress-linear
              :model-value="progressBars[row.bar].value"
              :buffer-value="progressBars[row.bar].bufferValue"
              color="primary"
              bg-color="#eef1f6"
              height="22"
              rounded="0"
              class="check-row__bar"
            >
              <span class="bar-label">
                <span class="bar-label__count"
                  >{{ progressBars[row.bar].versicherungen }} / 45</span
                >
                <span class="bar-label__text d-none d-sm-inline">
                  Versicherer geprüft
                </span>
              </span>
            </v-progress-linear>
            <div class="check-row__pct">
              {{ progressBars[row.bar].value }}&nbsp;%
            </div>
          </div>
        </div>
      </div>

      <footer class="loader-foot">
        <v-icon
          icon="mdi-clock-time-three-outline"
          size="14"
          class="mr-1"
        />
        Sekundenschnell &amp; vollständig anonym &mdash; ohne Datenweitergabe.
      </footer>
    </v-card>
  </v-dialog>
</template>

<script setup>
  import {
    reactive,
    ref,
    computed,
    defineProps,
    onMounted,
    onBeforeUnmount,
  } from "vue";
  import { useMarketingDataStore } from "@/stores/marketingData";

  const marketingDataStore = useMarketingDataStore();
  void marketingDataStore;

  const props = defineProps({
    berechnung: Object,
  });

  const isLoading = ref(true);

  const progressBars = reactive(
    Object.fromEntries(
      Array.from({ length: 11 }, (_, index) => [
        `bar${index + 1}`,
        { value: 10, interval: null, bufferValue: 100, versicherungen: 0 },
      ])
    )
  );

  const ber = computed(() => props.berechnung || {});

  const yesNo = (v, yes = "Ja", no = "Nein") =>
    v == 1 ? yes : v == 0 ? no : "—";

  const rows = computed(() => [
    {
      bar: "bar1",
      label: "Geburtstag",
      value: `${ber.value.ber_tag}.${ber.value.ber_monat}.${ber.value.ber_jahr}`,
    },
    {
      bar: "bar2",
      label: "Aufbiss-Schiene",
      value: yesNo(ber.value.ber_aufbiss, "vorhanden", "nicht vorhanden"),
    },
    {
      bar: "bar3",
      label: "Laufende Behandlung",
      value: yesNo(ber.value.ber_zahnmass, "ja / angeraten", "nein"),
    },
    {
      bar: "bar4",
      label: "Parodontosebehandlung",
      value: yesNo(ber.value.ber_zb_3, "ja", "nein"),
    },
    {
      bar: "bar5",
      label: "Zahnfehlstellung",
      value: yesNo(ber.value.ber_zahnfehl, "vorhanden", "nein"),
    },
    {
      bar: "bar6",
      label: "Fehlende Zähne",
      value: yesNo(ber.value.ber_ze_ja, "ja", "nein"),
    },
    {
      bar: "bar7",
      label: "Anzahl Lücken",
      value: String(ber.value.ber_luecke ?? 0),
    },
    {
      bar: "bar8",
      label: "Lücken mitversichern",
      value: yesNo(ber.value.ber_luecke_vers, "ja", "nein"),
    },
    {
      bar: "bar9",
      label: "Anzahl Prothesen",
      value: String(ber.value.ber_zahnersetzt ?? 0),
    },
    {
      bar: "bar10",
      label: "Anzahl Kronen",
      value: String(ber.value.ber_kronen ?? 0),
    },
    {
      bar: "bar11",
      label: "Anzahl Brücken",
      value: String(ber.value.ber_bruecke ?? 0),
    },
  ]);

  const totalTarget = computed(() => rows.value.length * 45);
  const totalChecked = computed(() =>
    rows.value.reduce(
      (sum, r) => sum + (progressBars[r.bar]?.versicherungen || 0),
      0
    )
  );
  const overallPercent = computed(() => {
    const sum = rows.value.reduce(
      (s, r) => s + (progressBars[r.bar]?.value || 0),
      0
    );
    return Math.min(100, Math.round(sum / rows.value.length));
  });

  function startBuffer(bar, abstand) {
    clearInterval(progressBars[bar].interval);
    progressBars[bar].interval = setInterval(() => {
      const barData = progressBars[bar];
      if (barData.versicherungen < 44) {
        barData.versicherungen++;
        barData.value += 2;
      }
      if (barData.versicherungen >= 44) {
        barData.versicherungen = 45;
        barData.value = 100;
      }
    }, abstand);
  }

  onMounted(() => {
    Object.entries(progressBars).forEach(([key], index) => {
      startBuffer(key, 80 + index * 20);
    });
  });

  onBeforeUnmount(() => {
    Object.values(progressBars).forEach((bar) => {
      clearInterval(bar.interval);
    });
  });
</script>

<style scoped>
  .loader-shell {
    background: #f4f6f9;
    border: none;
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  .loader-head {
    background: #ffffff;
    border-bottom: 1px solid #d8dde5;
    padding: 18px 16px 14px;
  }
  .loader-head__inner {
    max-width: 1100px;
    margin: 0 auto;
  }

  .loader-eyebrow {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.8px;
    color: #5b6473;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      monospace;
    margin-bottom: 8px;
  }
  .loader-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #0f4c81;
    border-radius: 1px;
    margin-right: 8px;
  }

  .loader-title {
    font-size: clamp(18px, 3.6vw, 24px);
    font-weight: 700;
    line-height: 1.25;
    color: #0f172a;
    letter-spacing: -0.2px;
    margin: 0 0 6px;
  }
  .loader-title__sep {
    color: #c7ccd6;
    margin: 0 6px;
  }
  .loader-title__hint {
    color: #2e7d32;
    font-weight: 600;
  }

  .loader-sub {
    font-size: 14px;
    color: #4b5563;
    margin: 0 0 12px;
    max-width: 760px;
  }
  .text-pos {
    color: #2e7d32;
  }
  .text-neg {
    color: #c62828;
  }

  .loader-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 18px;
    margin-bottom: 10px;
  }
  .meta-item {
    display: inline-flex;
    align-items: center;
    font-size: 12.5px;
    color: #4b5563;
  }
  .meta-label {
    color: #6b7280;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      monospace;
    font-size: 11px;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    margin-right: 6px;
  }
  .meta-value {
    font-weight: 700;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }
  .meta-item--badge {
    background: #eef5ec;
    color: #2e7d32;
    border: 1px solid #c8e3c1;
    padding: 2px 8px;
    border-radius: 3px;
    font-weight: 600;
  }

  .loader-overall {
    border-radius: 0 !important;
  }

  .loader-list {
    flex: 1 1 auto;
    max-width: 1100px;
    width: 100%;
    margin: 0 auto;
    padding: 8px 16px 24px;
  }

  .check-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 10px 0;
    border-bottom: 1px solid #e5e9f0;
  }
  .check-row:last-child {
    border-bottom: none;
  }
  .check-row--done .check-row__pct {
    color: #2e7d32;
  }

  .check-row__main {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }
  .check-row__label {
    font-size: 13.5px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: 0.1px;
  }
  .check-row__value {
    font-size: 12.5px;
    color: #0f4c81;
    font-weight: 600;
    text-align: right;
    font-variant-numeric: tabular-nums;
    background: #eef3f9;
    border: 1px solid #d8e2ee;
    padding: 1px 8px;
    border-radius: 3px;
    white-space: nowrap;
    max-width: 60%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .check-row__bar-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .check-row__bar {
    flex: 1 1 auto;
  }
  .check-row__pct {
    flex: 0 0 auto;
    width: 56px;
    text-align: right;
    font-size: 12.5px;
    font-weight: 700;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      monospace;
  }

  .bar-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: #ffffff;
    font-weight: 600;
    letter-spacing: 0.2px;
  }
  .bar-label__count {
    font-variant-numeric: tabular-nums;
  }

  .loader-foot {
    background: #ffffff;
    border-top: 1px solid #e5e9f0;
    padding: 10px 16px;
    font-size: 12px;
    color: #6b7280;
    text-align: center;
  }

  @media (min-width: 720px) {
    .loader-head {
      padding: 24px 24px 18px;
    }
    .loader-list {
      padding: 12px 24px 32px;
    }
    .check-row {
      grid-template-columns: 280px 1fr;
      gap: 16px;
      align-items: center;
      padding: 8px 0;
    }
    .check-row__value {
      max-width: 160px;
    }
  }
</style>
